
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Mail, Phone, ChevronUp } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import LogoSvg from './navigation/LogoSvg';

const Footer = () => {
  const { currentLanguage, getLocalizedPath } = useLanguage();
  const t = translations[currentLanguage];
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(false);

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    // Delay footer animation for a smoother entry
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <footer className={`bg-gradient-to-b from-secondary/80 to-black text-white pt-16 pb-8 relative overflow-hidden transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      {/* Background gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-900/10 to-black/30 z-0"></div>
      
      {/* Animated lines - decorative element */}
      <div className="absolute inset-0 z-0 opacity-10">
        <div className="absolute inset-0 animate-scan-line bg-gradient-to-r from-transparent via-purple-400/20 to-transparent"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row">
          {/* Text content - Left side */}
          <div className="lg:w-3/5 lg:pr-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="footer-column animate-fade-up" style={{ animationDelay: '100ms' }}>
                <h3 className="text-xl font-bold mb-4 text-white font-future bg-clip-text text-transparent bg-gradient-to-r from-white to-purple-300">Future Level</h3>
                <p className="text-gray-300 mb-4 leading-relaxed text-sm md:text-base">
                  {t.footerDescription}
                </p>
                <div className="flex space-x-4">
                  <a 
                    href="https://facebook.com" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-gray-300 hover:text-purple-400 transition-colors hover:scale-110 transform duration-200 group"
                    aria-label="Facebook"
                  >
                    <Facebook size={20} className="group-hover:animate-pulse" />
                  </a>
                  <a 
                    href="https://instagram.com" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-gray-300 hover:text-purple-400 transition-colors hover:scale-110 transform duration-200 group"
                    aria-label="Instagram"
                  >
                    <Instagram size={20} className="group-hover:animate-pulse" />
                  </a>
                </div>
              </div>

              <div className="footer-column animate-fade-up" style={{ animationDelay: '200ms' }}>
                <h3 className="text-xl font-bold mb-4 text-white font-future bg-clip-text text-transparent bg-gradient-to-r from-white to-purple-300">{t.services}</h3>
                <ul className="space-y-2">
                  <li className="transform transition-transform duration-200 hover:translate-x-1">
                    <Link to={getLocalizedPath("services/product-videos")} className="text-gray-300 hover:text-purple-400 transition-colors flex items-center group">
                      <span className="block w-0 group-hover:w-2 h-0.5 bg-purple-500 mr-0 group-hover:mr-1 transition-all duration-200"></span>
                      {t.productVideos}
                    </Link>
                  </li>
                  <li className="transform transition-transform duration-200 hover:translate-x-1">
                    <Link to={getLocalizedPath("services/social-media-ads")} className="text-gray-300 hover:text-purple-400 transition-colors flex items-center group">
                      <span className="block w-0 group-hover:w-2 h-0.5 bg-purple-500 mr-0 group-hover:mr-1 transition-all duration-200"></span>
                      {t.socialMediaAds}
                    </Link>
                  </li>
                </ul>
              </div>

              <div className="footer-column animate-fade-up" style={{ animationDelay: '300ms' }}>
                <h3 className="text-xl font-bold mb-4 text-white font-future bg-clip-text text-transparent bg-gradient-to-r from-white to-purple-300">{t.contact}</h3>
                <address className="not-italic text-gray-300">
                  <p className="mb-1">Future Level</p>
                  <p className="mb-1">Zumhofstrasse, 6010 Kriens</p>
                  <p className="mb-1 flex items-center group">
                    <Mail size={16} className="mr-2 text-purple-400 group-hover:animate-pulse" />
                    <a href="mailto:info@future-level.ch" className="hover:text-purple-400 transition-colors">
                      info@future-level.ch
                    </a>
                  </p>
                  <p className="flex items-center group">
                    <Phone size={16} className="mr-2 text-purple-400 group-hover:animate-pulse" />
                    <a href="tel:+41415391613" className="hover:text-purple-400 transition-colors">
                      +41 41 539 16 13
                    </a>
                  </p>
                </address>
              </div>
            </div>

            <hr className="border-purple-900/50 my-8" />

            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm mb-4 md:mb-0">
                &copy; {currentYear} Future Level. {t.allRightsReserved}
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to={getLocalizedPath("legal")} className="text-gray-400 text-sm hover:text-purple-400 transition-colors">
                  {t.impressum}
                </Link>
                <Link to={getLocalizedPath("privacy")} className="text-gray-400 text-sm hover:text-purple-400 transition-colors">
                  {t.privacy}
                </Link>
                <Link to={getLocalizedPath("terms")} className="text-gray-400 text-sm hover:text-purple-400 transition-colors">
                  {t.terms}
                </Link>
              </div>
            </div>
          </div>
          
          {/* Logo container - moved higher to match text alignment */}
          <div className={`lg:w-2/5 ${isMobile ? 'mt-8' : ''} relative h-[300px] lg:h-auto rounded-lg overflow-hidden footer-video-container animate-fade-in flex flex-col justify-center`}>
            <div className="flex items-center justify-center">
              <LogoSvg className="w-48 h-auto animate-pulse-slow" />
            </div>
            
            <div className="mt-4 text-white text-center">
              <h4 className="text-lg font-future bg-clip-text text-transparent bg-gradient-to-r from-white to-purple-300">Future Level</h4>
              <p className="text-sm text-gray-300">AI-Powered Video Marketing</p>
            </div>
          </div>
        </div>
      </div>

      {/* Back to top button */}
      <div className="absolute bottom-4 right-4 md:bottom-8 md:right-8 z-20">
        <Button 
          onClick={handleScrollToTop}
          size="icon"
          variant="outline"
          className="rounded-full bg-black/50 border border-purple-500/30 hover:bg-purple-900/30 text-purple-400 hover:text-purple-300 backdrop-blur-sm shadow-[0_0_10px_rgba(139,92,246,0.2)]"
          aria-label="Back to top"
        >
          <ChevronUp className="h-5 w-5" />
        </Button>
      </div>
    </footer>
  );
};

export default Footer;
